import { useMemo, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { partnerOffersRepo } from "@/data/repos/partnerOffersRepo";
import { czechPlural, reservationForms } from "@/lib/czech";
import { formatPrice } from "@/lib/money";
import { capacityRefusalMessage } from "@/features/offers/sales-locks";
import { isApiError } from "@/data/api/client";
import {
  canDeleteOffer,
  cancellationReachedCustomers,
  isKeptOnlyForHistory,
} from "@/domain/offer/rules";
import type { OfferCancellation, PartnerOffer } from "@/domain/offer/types";
import {
  partnerOffersQueryKey,
  useReservationAnswers,
} from "@/features/reservations/hooks/useReservationAnswers";

export { partnerOffersQueryKey };

function byStartTime(a: PartnerOffer, b: PartnerOffer): number {
  return a.startTime.getTime() - b.startTime.getTime();
}

export function useOfferCancellationPreview(offerId: number | null) {
  return useQuery<OfferCancellation>({
    queryKey: ["offer-cancellation-preview", offerId],
    queryFn: () => partnerOffersRepo.previewCancellation(offerId!),
    enabled: offerId !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

/** The customers are already dealt with — refunded, released or told — so a
 *  retry must not do it again; it resumes at the step that failed. */
export class OfferStillSellingAfterRefundsError extends Error {
  constructor(readonly tally: OfferCancellation) {
    super("Customers were already dealt with, but the offer still sells");
    this.name = "OfferStillSellingAfterRefundsError";
  }
}

function keptShareSentence(keptShareCount: number): string {
  return `Zákazníkům, kteří po bezplatné lhůtě zrušili ${keptShareCount} ${czechPlural(
    keptShareCount,
    reservationForms,
  )}, jsme dali vědět, že se termín nekoná; storno se jim nebude účtovat.`;
}

function whatWasDoneForCustomers(tally: OfferCancellation): string {
  const customerMoney =
    tally.refundAmountMinor === 0
      ? "zákazníkům nic nestrhneme a jejich částku uvolňujeme"
      : `zákazníkům vracíme ${formatPrice(tally.refundAmountMinor)}, u ostatních uvolňujeme částku`;
  return [
    tally.reservationCount > 0 && `Rezervace jsme zrušili: ${customerMoney}.`,
    tally.reservationCount === 0 &&
      tally.refundAmountMinor > 0 &&
      `Zákazníkům vracíme ${formatPrice(tally.refundAmountMinor)}.`,
    tally.keptShareCount > 0 && keptShareSentence(tally.keptShareCount),
    tally.refundFailedAmountMinor > 0 &&
      `Vrácení ${formatPrice(tally.refundFailedAmountMinor)} se zákazníkům nepodařilo.`,
  ]
    .filter(Boolean)
    .join(" ");
}

function stillSellingMessage(tally: OfferCancellation): string {
  return `${whatWasDoneForCustomers(tally)} Slevový termín se ale nepodařilo stáhnout z prodeje. Zkuste to prosím znovu — zákazníkům se nic podruhé nevyřizuje.`;
}

const keptForHistoryMessage =
  "Slevový termín se přestal prodávat. V seznamu zůstává, protože k němu patří záznamy o dřívějších rezervacích nebo nedokončených platbách; zrušení se už nikoho netýká.";

function cancelledMessage(tally: OfferCancellation): string {
  if (isKeptOnlyForHistory(tally)) return keptForHistoryMessage;
  return [
    "Slevový termín byl zrušen.",
    tally.reservationCount > 0 &&
      `Zrušili jste ${tally.reservationCount} ${czechPlural(
        tally.reservationCount,
        reservationForms,
      )} a zákazníkům jsme o tom dali vědět.`,
    tally.keptShareCount > 0 && keptShareSentence(tally.keptShareCount),
  ]
    .filter(Boolean)
    .join(" ");
}

function refundFailedMessage(tally: OfferCancellation): string {
  return `${cancelledMessage(tally)} Zákazníkům se ale nepodařilo vrátit ${formatPrice(
    tally.refundFailedAmountMinor,
  )}. Zrušte slevový termín prosím znovu — vrácení se zkusí znovu.`;
}

// Cancelling always asks the API first: it is the only place that knows who
// is still booked at this instant, and its tally decides what is left to do.
// Nobody reached and no reservation history means nothing to keep — the sleva
// is removed outright. Anybody reached — booked, or cancelled after the free
// window, or owed a refund — or any earlier reservation means the sleva merely
// stops selling, so the rows customers were told about and the money trail
// they carry survive. The cancel call itself already closes the sale, so a
// termín kept only for its history needs no second request to stop selling,
// and the API refusing that request for a confirmation activity that has
// started means the sale is already closed.
export function useCancelOffer() {
  const { refresh } = useReservationAnswers();
  const refundedOffers = useRef(new Map<number, OfferCancellation>());

  return useMutation({
    mutationFn: async (offerId: number): Promise<OfferCancellation> => {
      const alreadyRefunded = refundedOffers.current.get(offerId);
      const tally = alreadyRefunded ?? (await partnerOffersRepo.cancelReservations(offerId));
      const hasReachedCustomers = cancellationReachedCustomers(tally);
      if (hasReachedCustomers) refundedOffers.current.set(offerId, tally);

      if (hasReachedCustomers) {
        try {
          await partnerOffersRepo.stopSelling(offerId, new Date());
        } catch (error) {
          if (!isApiError(error, "SALE_CLOSES_AFTER_START")) {
            throw new OfferStillSellingAfterRefundsError(tally);
          }
        }
      } else if (canDeleteOffer(tally)) {
        try {
          await partnerOffersRepo.remove(offerId);
        } catch (error) {
          if (!isApiError(error, "OFFER_HAS_RESERVATIONS")) throw error;
          // The API holds rows this tally never saw — an older API that sends no
          // `hasReservationHistory`, or a reservation made since the preview.
          return { ...tally, hasReservationHistory: true };
        }
      }

      refundedOffers.current.delete(offerId);
      return tally;
    },
    onSuccess: (tally) => {
      if (tally.refundFailedAmountMinor > 0) toast.warning(refundFailedMessage(tally));
      else toast.success(cancelledMessage(tally));
      refresh();
    },
    onError: (error) => {
      if (error instanceof OfferStillSellingAfterRefundsError) {
        toast.error(stillSellingMessage(error.tally));
        refresh();
        return;
      }
      toast.error("Slevový termín se nepodařilo zrušit. Zkuste to prosím znovu.");
    },
  });
}

export function usePartnerOffers() {
  const { refresh } = useReservationAnswers();

  const offersQuery = useQuery({
    queryKey: partnerOffersQueryKey,
    queryFn: () => partnerOffersRepo.list(),
  });

  const offers = useMemo(
    () => [...(offersQuery.data ?? [])].sort(byStartTime),
    [offersQuery.data],
  );


  const setCapacity = useMutation({
    mutationFn: ({ slotId, capacity }: { slotId: number; capacity: number }) =>
      partnerOffersRepo.setCapacity(slotId, capacity),
    onSuccess: () => {
      toast.success("Počet míst uložen.");
      refresh();
    },
    onError: (error) =>
      toast.error(
        capacityRefusalMessage(error) ?? "Počet míst se nepodařilo uložit. Zkuste to prosím znovu.",
      ),
  });

  const cancelOffer = useCancelOffer();

  return {
    offers,
    isLoading: offersQuery.isLoading,
    error: offersQuery.error,
    refetch: () => void offersQuery.refetch(),
    setCapacity,
    cancelOffer,
  };
}
